import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Button } from '@sentinel/ui';
import type { ApiKeyItem } from '../use-api-keys';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  list: { display: 'grid', gap: '0.5rem', margin: 0, padding: 0, listStyle: 'none' },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'flex-start',
    gap: '1rem',
    paddingTop: '0.875rem',
    paddingBottom: '0.875rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.5rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
  },
  rowRevoked: { opacity: 0.6 },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  title: { fontWeight: 600 },
  prefix: {
    fontFamily: sentinel.fontMono,
    fontSize: '0.8125rem',
    color: sentinel.textSubtle,
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '0.25rem',
    backgroundColor: sentinel.surfaceSunken,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
  },
  meta: {
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem 1rem',
    marginTop: '0.375rem',
  },
  actions: { display: 'flex', gap: '0.375rem', alignItems: 'center' },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
  },
  badgeActive: {
    backgroundColor: 'oklch(72.10% 0.1500 152.00 / 0.10)',
    color: 'oklch(72.10% 0.1500 152.00)',
  },
  badgeRevoked: {
    backgroundColor: sentinel.borderDefault,
    color: sentinel.textSubtle,
  },
  badgeRotating: {
    backgroundColor: `color-mix(in oklab, ${sentinel.textWarning} 10%, transparent)`,
    color: sentinel.textWarning,
  },
});

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export interface ApiKeyListProps {
  items: ApiKeyItem[];
  busyId?: string | null;
  onRotate: (item: ApiKeyItem) => void;
  onRevoke: (item: ApiKeyItem) => void;
}

export function ApiKeyList({ items, busyId, onRotate, onRevoke }: ApiKeyListProps) {
  const { t } = useTranslation('credentials');

  return (
    <ul {...stylex.props(styles.list)} data-testid="api-keys-list">
      {items.map((k) => {
        const isRevoked = k.status === 'revoked';
        return (
          <li
            key={k.id}
            {...stylex.props(styles.row, isRevoked && styles.rowRevoked)}
            data-testid={`api-key-row-${k.id}`}
          >
            <div>
              <div {...stylex.props(styles.titleRow)}>
                <span {...stylex.props(styles.title)}>
                  {k.label || t('row_untitled')}
                </span>
                <span {...stylex.props(styles.prefix)}>{k.key_prefix}…</span>
                <span
                  {...stylex.props(
                    styles.badge,
                    k.status === 'active' && styles.badgeActive,
                    k.status === 'revoked' && styles.badgeRevoked,
                    k.status === 'rotating' && styles.badgeRotating,
                  )}
                >
                  {t(`status_${k.status}`)}
                </span>
              </div>
              <div {...stylex.props(styles.meta)}>
                <span>
                  {t('row_created_label')}: {formatDate(k.created_at)}
                </span>
                <span>
                  {t('row_last_used_label')}:{' '}
                  {k.last_used_at ? formatDate(k.last_used_at) : t('row_last_used_never')}
                </span>
              </div>
            </div>
            <div {...stylex.props(styles.actions)}>
              {!isRevoked && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onRotate(k)}
                  disabled={busyId === k.id}
                  data-testid={`api-key-rotate-${k.id}`}
                >
                  {t('row_rotate_button')}
                </Button>
              )}
              {!isRevoked && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => onRevoke(k)}
                  disabled={busyId === k.id}
                  data-testid={`api-key-revoke-${k.id}`}
                >
                  {t('row_revoke_button')}
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
